import type { ApiKey, RelationListItem } from '@verybigthings/trackframe-shared/schemas';
import { cn } from '@/lib/utils';

/**
 * Can the deployed track push its manifest on boot? `PUT /tracks/:id` admits
 * two credentials (HUB-089 D2): the key bound to the track, and any key whose
 * principal `user:apikey:<id>` holds `track#admin` — the creator tuple, or a
 * repair tuple written by hand. Revoked keys fail the gate before either door.
 * System-admin keys would pass too, but the key list does not expose that
 * flag and HUB-089 forbids them as deploy credentials, so they do not count.
 */
export type SelfRegistration =
  | { ok: true; via: 'bound' | 'admin'; key: ApiKey }
  | { ok: false };

export function selfRegistration(trackId: string, keys: ApiKey[], relations: RelationListItem[]): SelfRegistration {
  const live = keys.filter((k) => !k.revokedAt);
  const bound = live.find((k) => k.trackId === trackId);
  if (bound) return { ok: true, via: 'bound', key: bound };

  const adminKeyIds = new Set(
    relations
      .filter((r) => r.object === `track:${trackId}` && r.relation === 'admin')
      .map((r) => r.subject)
      .filter((s) => s.startsWith('user:apikey:'))
      .map((s) => s.slice('user:apikey:'.length)),
  );
  const admin = live.find((k) => adminKeyIds.has(k.id));
  if (admin) return { ok: true, via: 'admin', key: admin };

  return { ok: false };
}


function KeyName({ k }: { k: ApiKey }) {
  return (
    <>
      <span className="font-medium text-foreground">{k.name}</span>{' '}
      <span className="font-mono text-xs">{k.prefix}…</span>
    </>
  );
}

export function SelfRegistrationStatus({ state }: { state: SelfRegistration }) {
  return (
    <div className="space-y-1 text-sm">
      <p className={cn('flex items-center gap-2 font-medium', state.ok ? 'text-success' : 'text-destructive')}>
        <span aria-hidden="true" className={cn('size-2 rounded-full', state.ok ? 'bg-success' : 'bg-destructive')} />
        {state.ok ? 'Track can self-register' : 'Track cannot self-register'}
      </p>
      <p className="text-muted-foreground">
        {!state.ok
          ? 'No live API key is bound to this track or holds its admin relation. The deployed track’s boot push is rejected and the hub keeps a stale copy of the spec. Bind the key the track deploys with, or mint one.'
          : state.via === 'bound'
            ? <>Through its bound key <KeyName k={state.key} />.</>
            : <>Through the admin relation held by key <KeyName k={state.key} />. Bind that key so the grant is structural and no longer depends on a permission tuple.</>}
      </p>
    </div>
  );
}
